import * as React from 'react';
import { useContext, useState } from 'react';
import AspectRatio from '@mui/joy/AspectRatio';
import Card from '@mui/joy/Card';
import Typography from '@mui/joy/Typography';
import CardContent from '@mui/joy/CardContent';
import CardOverflow from '@mui/joy/CardOverflow';
import Box from '@mui/joy/Box';
import Chip from '@mui/joy/Chip';
import Link from '@mui/joy/Link';
import IconButton from '@mui/joy/IconButton';
import Button from '@mui/joy/Button';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimerIcon from '@mui/icons-material/Timer';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getCategoryDisplay, getRelationDisplay, RELATIONS } from '../../third_parties/map/placeAutoComplete';
import SpotReviewContext from '../../context/spot/spotReviewContext';
import SpotReviewComposerModal from '../spots/SpotReviewComposer/SpotReviewComposerModal';
import SpotAddressLine from '../spots/SpotAddressLine';

// Shared card for rendering a spot review. Used in both the trip-detail page
// and the draft preview on the create-trip page. Keeping a single component
// ensures future tweaks show up in both places automatically.
//
// Accepts a review that may be either a server review (snake_case fields) or
// a draft (camelCase fields).
const TripSpotItem = ({ spotReview, fromPath, isDraft, draftIndex, readOnly, onDetach }) => {
    const {
        removeDraftSpotReview,
        addDraftSubSpotReview,
        removeDraftSubSpotReview,
    } = useContext(SpotReviewContext);
    const [showAddSubPills, setShowAddSubPills] = useState(false);
    // Per-sub-row expansion: opens the row to reveal price + content.
    // Keyed by sub index within the normalized subReviews array below.
    const [expandedSubIdx, setExpandedSubIdx] = useState(null);

    const {
        title,
        content,
        photos,
        created_at,
        // server-shape time fields
        visit_start_time,
        visit_duration,
        visit_from,
        visit_to,
        // draft-shape time fields
        visitStartTime,
        visitDuration,
        category,
        price,
    } = spotReview;

    const hasPhoto = photos && photos.length > 0 && photos[0] && photos[0].image_link;
    const categoryDisplay = getCategoryDisplay(category);

    // Normalize place fields across server (snake_case) + draft (camelCase).
    // On GetTrip, BE populates spot_name/spot_address via Google Places so
    // we can show them in the collapsed state.
    const mainSpotName = spotReview.spot_name || spotReview.placeName || null;
    const mainSpotAddress = spotReview.spot_address || spotReview.placeAddress || null;
    const mainPlaceId = spotReview.spot_place_id || spotReview.placeId || null;

    const reviewHref = spotReview.id
        ? `/review/${spotReview.id}${fromPath ? `?from=${encodeURIComponent(fromPath)}` : ''}`
        : null;

    const startMs = visit_start_time
        ? new Date(visit_start_time).getTime()
        : visitStartTime
            ? new Date(visitStartTime).getTime()
            : (visit_from ? visit_from * 1000 : null);
    const durationMin = visit_duration
        ? parseInt(visit_duration)
        : visitDuration
            ? parseInt(visitDuration)
            : (visit_from && visit_to && visit_to > visit_from
                ? Math.round((visit_to - visit_from) / 60)
                : null);

    const timeString = created_at
        ? new Date(created_at * 1000).toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        })
        : null;

    const formatDuration = (m) => {
        if (!m) return null;
        if (m >= 1440) return 'Full day';
        if (m >= 60) return m % 60 === 0 ? `${m / 60}h` : `${Math.floor(m / 60)}h ${m % 60}m`;
        return `${m}m`;
    };

    const handleDelete = (e) => {
        e.preventDefault();
        e.stopPropagation();
        removeDraftSpotReview(draftIndex);
    };

    // Sub-reviews live under different field names depending on whether the
    // review came from the server (sub_reviews) or is an unsaved draft
    // (subReviews). Normalize here.
    const subReviews = spotReview.sub_reviews || spotReview.subReviews || [];

    // The "+ Add related" UI is only meaningful for:
    // - draft parents that are NOT a picked-existing review (those already
    //   exist server-side and we don't want trip-flow to mutate them),
    // - saved reviews whose id we know.
    // Suppressed in read-only contexts (discover / local finds) — editing
    // lives on the "My Journeys" page.
    const canAddSubReview =
        !readOnly &&
        ((isDraft && !spotReview.existingReviewId) || (!isDraft && !!spotReview.id));

    const handleRemoveDraftSub = (subIdx) => (e) => {
        e.preventDefault();
        e.stopPropagation();
        removeDraftSubSpotReview(draftIndex, subIdx);
    };

    // For a draft parent, the composer hands the composed sub-review back
    // via onDraftSubmit instead of dispatching. We stash it on the parent.
    const onDraftSubSubmit = (relation) => (composed) => {
        addDraftSubSpotReview(draftIndex, { ...composed, relation });
    };

    if (spotReview.deleted) {
        return (
            <Card
                orientation="horizontal"
                variant="outlined"
                sx={{
                    width: '100%',
                    p: 2,
                    bgcolor: 'neutral.softBg',
                    color: 'text.tertiary',
                    fontStyle: 'italic',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: 80,
                }}
            >
                <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                    This review has been deleted
                </Typography>
            </Card>
        );
    }

    const showRelatedSection = canAddSubReview || subReviews.length > 0;

    return (
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
        <Card
            orientation={hasPhoto ? "horizontal" : "vertical"}
            variant="outlined"
            sx={{
                width: '100%',
                p: 0,
                overflow: 'hidden',
                cursor: isDraft ? 'default' : 'pointer',
                transition: 'transform 200ms ease, box-shadow 200ms ease',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 'md',
                },
                ...(showRelatedSection && {
                    borderBottomLeftRadius: 0,
                    borderBottomRightRadius: 0,
                    borderBottom: 'none',
                }),
            }}
        >
            {hasPhoto && (
                <CardOverflow>
                    <AspectRatio ratio="1" sx={{ width: { xs: 120, sm: 180 }, minWidth: { xs: 120, sm: 180 } }}>
                        <img
                            src={photos[0].image_link}
                            srcSet={photos[0].image_link}
                            loading="lazy"
                            alt={title}
                        />
                    </AspectRatio>
                </CardOverflow>
            )}
            <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography level="title-lg" sx={{ fontWeight: 600 }}>
                        {isDraft || !reviewHref ? (
                            title
                        ) : (
                            <Link overlay underline="none" href={reviewHref} sx={{ color: 'inherit' }}>
                                {title}
                            </Link>
                        )}
                    </Typography>
                    {category && (
                        <Chip
                            size="sm"
                            variant="soft"
                            color="neutral"
                            startDecorator={categoryDisplay.icon}
                            sx={{ fontSize: 11, '--Chip-minHeight': '20px' }}
                        >
                            {categoryDisplay.label}
                        </Chip>
                    )}
                </Box>
                {/* Place name + address with Maps link. Shown inline on the
                    parent card (no expand gate) so riders of the trip see
                    where the review is grounded at a glance. */}
                {(mainSpotName || mainSpotAddress) && (
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        <SpotAddressLine
                            placeName={mainSpotName}
                            placeAddress={mainSpotAddress}
                            placeId={mainPlaceId}
                        />
                    </Typography>
                )}
                {content && (
                    <Typography
                        level="body-sm"
                        sx={{
                            color: 'text.secondary',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                        }}
                    >
                        {content}
                    </Typography>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography
                        level="body-xs"
                        startDecorator={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                        sx={{ color: 'text.tertiary' }}
                    >
                        {startMs
                            ? new Date(startMs).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                            : (timeString || '--')}
                    </Typography>
                    <Typography
                        level="body-xs"
                        startDecorator={<TimerIcon sx={{ fontSize: 14 }} />}
                        sx={{ color: 'text.tertiary' }}
                    >
                        {durationMin ? `Est. stay: ${formatDuration(durationMin)}` : 'Est. stay: --'}
                    </Typography>
                    {!!price && Number(price) !== 0 && (
                        <Typography
                            level="body-xs"
                            startDecorator={<PaidOutlinedIcon sx={{ fontSize: 14 }} />}
                            sx={{ color: 'text.tertiary' }}
                        >
                            ~${price}/person
                        </Typography>
                    )}
                    {(isDraft || onDetach) && (
                        <>
                            <Box sx={{ flexGrow: 1 }} />
                            <IconButton
                                variant="outlined"
                                color="danger"
                                size="sm"
                                onClick={isDraft ? handleDelete : (e) => { e.preventDefault(); e.stopPropagation(); onDetach(spotReview.id); }}
                                aria-label={isDraft ? "delete draft spot review" : "remove from trip"}
                                sx={{ position: 'relative', zIndex: 2 }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </>
                    )}
                </Box>
            </CardContent>
        </Card>

        {/* Related spots: sub-reviews attached to this parent. Renders for
            both drafts (camelCase subReviews) and saved reviews (snake_case
            sub_reviews). Draft rows get a × to remove; saved rows are
            clickable. "+ Add related" is hidden by default and expands into
            three pill triggers (parking / viewpoint / trailhead). */}
        {showRelatedSection && (
            <Box
                sx={{
                    width: '100%',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderTop: 'none',
                    borderBottomLeftRadius: 8,
                    borderBottomRightRadius: 8,
                    bgcolor: 'neutral.softBg',
                    px: 2,
                    py: 1.25,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.75,
                }}
            >
                {subReviews.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {subReviews.map((sub, subIdx) => {
                            const rd = getRelationDisplay(sub.relation);
                            // Normalize across draft (camelCase) + server (snake_case)
                            // shapes so the same row renders in both contexts.
                            const subTitle = sub.title || '(untitled)';
                            const subSpot = sub.spot_name || sub.placeName || null;
                            const subAddress = sub.spot_address || sub.placeAddress || null;
                            const subPlaceId = sub.spot_place_id || sub.placeId || null;
                            const subContent = sub.content || '';
                            const subPrice = (sub.price !== undefined && sub.price !== null && sub.price !== '')
                                ? sub.price : null;
                            const isExpanded = expandedSubIdx === subIdx;
                            // Collapsed row leads with place name + address +
                            // price (mirrors the parent-card format). Title
                            // and content are revealed on expand.
                            const hasDetailsToShow = !!subContent || (!!subTitle && subTitle !== '(untitled)');
                            const rowKey = (!isDraft && sub.id) ? sub.id : `draft-sub-${subIdx}`;

                            const handleToggle = (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setExpandedSubIdx(isExpanded ? null : subIdx);
                            };
                            const headerRow = (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        py: 0.5,
                                        minWidth: 0,
                                    }}
                                >
                                    {rd && (
                                        <Chip
                                            size="sm"
                                            variant="soft"
                                            color="neutral"
                                            startDecorator={rd.icon}
                                            sx={{ fontSize: 11, '--Chip-minHeight': '20px', flexShrink: 0 }}
                                        >
                                            {rd.label}
                                        </Chip>
                                    )}
                                    {/* Collapsed row mirrors the parent card:
                                        📍 **name** — address (maps link),
                                        with price tacked on. Title + content
                                        live behind the expand arrow. */}
                                    <Typography
                                        level="body-xs"
                                        sx={{
                                            color: 'text.tertiary',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            flexShrink: 1,
                                            minWidth: 0,
                                        }}
                                    >
                                        <SpotAddressLine
                                            placeName={subSpot}
                                            placeAddress={subAddress}
                                            placeId={subPlaceId}
                                            nameHref={!isDraft && sub.id
                                                ? `/review/${sub.id}${fromPath ? `?from=${encodeURIComponent(fromPath)}` : ''}`
                                                : undefined}
                                        />
                                    </Typography>
                                    {subPrice != null && (
                                        <Typography
                                            level="body-xs"
                                            sx={{ color: 'text.tertiary', flexShrink: 0 }}
                                        >
                                            ~${subPrice}/person
                                        </Typography>
                                    )}
                                    <Box sx={{ flexGrow: 1 }} />
                                    {hasDetailsToShow && (
                                        <IconButton
                                            size="sm"
                                            variant="plain"
                                            color="neutral"
                                            onClick={handleToggle}
                                            aria-label={isExpanded ? 'collapse details' : 'expand details'}
                                            sx={{ minHeight: 22, minWidth: 22, '--IconButton-size': '22px' }}
                                        >
                                            {isExpanded
                                                ? <ExpandLessIcon sx={{ fontSize: 18 }} />
                                                : <ExpandMoreIcon sx={{ fontSize: 18 }} />}
                                        </IconButton>
                                    )}
                                    {isDraft && (
                                        <IconButton
                                            size="sm"
                                            variant="plain"
                                            color="neutral"
                                            onClick={handleRemoveDraftSub(subIdx)}
                                            aria-label="remove related spot"
                                            sx={{ minHeight: 22, minWidth: 22, '--IconButton-size': '22px' }}
                                        >
                                            <CloseIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    )}
                                </Box>
                            );

                            const detailsPanel = isExpanded && hasDetailsToShow ? (
                                <Box sx={{ pl: 1, pb: 0.75, pt: 0.25 }}>
                                    {/* Expanded: the user's title · their
                                        written content. Address + price are
                                        already shown in the collapsed row. */}
                                    <Typography
                                        level="body-sm"
                                        sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}
                                    >
                                        {subTitle && subTitle !== '(untitled)' && (
                                            <strong>{subTitle}</strong>
                                        )}
                                        {subTitle && subTitle !== '(untitled)' && subContent && ' · '}
                                        {subContent}
                                    </Typography>
                                </Box>
                            ) : null;

                            return (
                                <Box key={rowKey}>
                                    {headerRow}
                                    {detailsPanel}
                                </Box>
                            );
                        })}
                    </Box>
                )}
                {canAddSubReview && (
                    showAddSubPills ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                            {Object.entries(RELATIONS).map(([key, rd]) => {
                                // Saved parent: pass parentId+relation so composer POSTs directly.
                                // Draft parent: pass onDraftSubmit so the composer hands the
                                // composed review back and we stash it on the parent draft.
                                const isSavedParent = !isDraft && !!spotReview.id;
                                return (
                                    <SpotReviewComposerModal
                                        key={key}
                                        parentId={isSavedParent ? spotReview.id : undefined}
                                        // Always pass `relation` so the composer
                                        // switches to its lite 2-step flow. For
                                        // saved parents it's also used to wire
                                        // the POST payload; for draft parents
                                        // we still stash it via onDraftSubmit.
                                        relation={key}
                                        onDraftSubmit={isSavedParent ? undefined : onDraftSubSubmit(key)}
                                        triggerLabel={`+ ${rd.label}`}
                                        triggerSx={{
                                            textTransform: 'none',
                                            borderRadius: 999,
                                            borderStyle: 'dashed',
                                            borderWidth: 1,
                                            px: 1.5,
                                            py: 0.25,
                                            minHeight: 0,
                                            fontSize: '0.8rem',
                                            fontWeight: 500,
                                            color: 'primary.main',
                                            borderColor: 'primary.light',
                                        }}
                                    />
                                );
                            })}
                            <Button
                                size="sm"
                                variant="plain"
                                color="neutral"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowAddSubPills(false); }}
                                sx={{ fontSize: '0.75rem', minHeight: 0, py: 0.25 }}
                            >
                                Cancel
                            </Button>
                        </Box>
                    ) : (
                        <Button
                            size="sm"
                            variant="plain"
                            color="primary"
                            startDecorator={<AddIcon sx={{ fontSize: 14 }} />}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowAddSubPills(true); }}
                            sx={{
                                alignSelf: 'flex-start',
                                fontSize: '0.8rem',
                                fontWeight: 500,
                                minHeight: 0,
                                py: 0.25,
                            }}
                        >
                            Add related (parking, viewpoint, trailhead)
                        </Button>
                    )
                )}
            </Box>
        )}
        </Box>
    );
};

export default TripSpotItem;
